/**
 * PWA Utilities for Dynamic Tenant Branding
 * Updates PWA manifest, icons, and meta tags based on tenant configuration
 */

/**
 * Update PWA manifest with tenant-specific branding
 * @param {Object} tenant - Tenant configuration object
 */
// Simple cache to avoid refetching icons
const iconCache = new Map();

const toAbsoluteUrl = (url, origin) => (url?.startsWith('http') ? url : new URL(url || '/logo.png', origin).href);

const fetchIconDataUrl = async (url) => {
  if (!url) return { dataUrl: null, ok: false };
  if (iconCache.has(url)) return iconCache.get(url);
  try {
    // Check if URL is external (not from current origin)
    const isExternal = url?.startsWith('http') && !url.startsWith(window.location.origin);
    
    // For external URLs, use backend proxy to bypass CORS
    const fetchUrl = isExternal 
      ? `/api/v1/image-proxy?url=${encodeURIComponent(url)}`
      : url;

    const res = await fetch(fetchUrl);
    if (!res.ok) return { dataUrl: null, ok: false };
    
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
    const result = { dataUrl, ok: true };
    iconCache.set(url, result);
    return result;
  } catch (e) {
    console.warn('[PWA] Failed to fetch icon:', url, e.message);
    return { dataUrl: null, ok: false };
  }
};

export const updatePWAManifest = async (tenant) => {
  if (!tenant || typeof window === 'undefined') return;

  const origin = window.location.origin;
  const tenantKey = (tenant.id || tenant.name || 'default').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const fallbackLogoPath = `/${tenantKey}-logo.png`;
  // Use logo from tenant config if available, otherwise fall back to generated path
  const logoRaw = tenant.branding?.logo || tenant.logo || fallbackLogoPath;
  const logoAbs = toAbsoluteUrl(logoRaw, origin);

  // Try to inline as data URL to avoid CORS/install issues; fallback to absolute URL
  const defaultIconAbs = toAbsoluteUrl('/icon-512.png', origin);
  const { dataUrl: logoData, ok: logoOk } = await fetchIconDataUrl(logoAbs);
  const iconSrc = logoData || (logoOk ? logoAbs : defaultIconAbs);

  const manifest = {
    name: tenant.name || 'Smart Nurse AI',
    short_name: tenant.name?.split(' ')[0] || 'Smart Nurse',
    description: tenant.tagline || 'Administrative portal for hospital management system',
    // Use absolute URLs because the manifest is served from a blob URL
    start_url: `${origin}/`,
    display: 'standalone',
    background_color: '#ffffff',
    theme_color: tenant.branding?.primaryColor || '#2563eb',
    orientation: 'any',
    icons: [
      {
        src: iconSrc,
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any maskable'
      },
      {
        src: iconSrc,
        sizes: '512x512',
        type: 'image/png',
        purpose: 'any maskable'
      }
    ],
    categories: ['health', 'medical', 'productivity', 'business'],
    scope: `${origin}/`,
    prefer_related_applications: false
  };

  // Convert manifest to JSON string
  const manifestString = JSON.stringify(manifest);
  const blob = new Blob([manifestString], { type: 'application/json' });
  const manifestURL = URL.createObjectURL(blob);

  // Remove existing manifest link if present
  const existingManifest = document.querySelector('link[rel="manifest"]');
  if (existingManifest) {
    existingManifest.remove();
  }

  // Add new manifest link
  const manifestLink = document.createElement('link');
  manifestLink.rel = 'manifest';
  manifestLink.href = manifestURL;
  document.head.appendChild(manifestLink);

  console.log('[PWA] Manifest updated for tenant:', tenant.id);
};

/**
 * Update favicon and apple touch icons with tenant logo
 * @param {Object} tenant - Tenant configuration object
 */
export const updatePWAIcons = async (tenant) => {
  if (!tenant || typeof window === 'undefined') return;

  const origin = window.location.origin;
  const tenantKey = (tenant.id || tenant.name || 'default').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const fallbackLogoPath = `/${tenantKey}-logo.png`;
  // Use logo from tenant config if available, otherwise fall back to generated path
  const logoRaw = tenant.branding?.logo || tenant.logo || fallbackLogoPath;
  const qrRaw = tenant.branding?.['qr-logo'] || tenant.qrLogo || logoRaw;
  const logoAbs = toAbsoluteUrl(logoRaw, origin);
  const qrAbs = toAbsoluteUrl(qrRaw, origin);

  // Inline if possible to avoid CORS issues on install
  const defaultIconAbs = toAbsoluteUrl('/icon-512.png', origin);
  const { dataUrl: logoData, ok: logoOk } = await fetchIconDataUrl(logoAbs);
  const { dataUrl: qrData, ok: qrOk } = await fetchIconDataUrl(qrAbs);
  const logoHref = logoData || (logoOk ? logoAbs : defaultIconAbs);
  const qrHref = qrData || (qrOk ? qrAbs : logoHref);

  // Remove all existing favicon and icon links
  const existingIcons = document.querySelectorAll('link[rel*="icon"]');
  existingIcons.forEach(icon => icon.remove());

  // Add new favicon (for browsers)
  const favicon = document.createElement('link');
  favicon.rel = 'icon';
  favicon.type = 'image/png';
  favicon.href = logoHref;
  document.head.appendChild(favicon);

  // Add shortcut icon (for older browsers)
  const shortcutIcon = document.createElement('link');
  shortcutIcon.rel = 'shortcut icon';
  shortcutIcon.type = 'image/png';
  shortcutIcon.href = logoHref;
  document.head.appendChild(shortcutIcon);

  // Update or create apple touch icon
  let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
  if (appleTouchIcon) {
    appleTouchIcon.href = qrHref;
  } else {
    appleTouchIcon = document.createElement('link');
    appleTouchIcon.rel = 'apple-touch-icon';
    appleTouchIcon.href = qrHref;
    document.head.appendChild(appleTouchIcon);
  }

  console.log('[PWA] Icons updated for tenant:', tenant.id, 'Logo:', logoAbs);
};

/**
 * Update theme color meta tag
 * @param {Object} tenant - Tenant configuration object
 */
export const updateThemeColor = (tenant) => {
  if (!tenant || typeof window === 'undefined') return;

  const themeColor = tenant.branding?.primaryColor || '#2563eb';

  // Update theme-color meta tag
  let themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', themeColor);
  } else {
    themeColorMeta = document.createElement('meta');
    themeColorMeta.name = 'theme-color';
    themeColorMeta.content = themeColor;
    document.head.appendChild(themeColorMeta);
  }

  // Update apple-mobile-web-app-status-bar-style
  let statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  if (statusBarMeta) {
    statusBarMeta.setAttribute('content', 'default');
  }

  console.log('[PWA] Theme color updated:', themeColor);
};

/**
 * Update page title with tenant name
 * @param {Object} tenant - Tenant configuration object
 */
export const updatePageTitle = (tenant) => {
  if (!tenant || typeof window === 'undefined') return;

  const title = tenant.name || 'Smart Nurse';
  document.title = title;

  // Update apple-mobile-web-app-title
  let appTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (appTitleMeta) {
    appTitleMeta.setAttribute('content', title);
  } else {
    appTitleMeta = document.createElement('meta');
    appTitleMeta.name = 'apple-mobile-web-app-title';
    appTitleMeta.content = title;
    document.head.appendChild(appTitleMeta);
  }

  console.log('[PWA] Page title updated:', title);
};

/**
 * Initialize all PWA branding updates
 * @param {Object} tenant - Tenant configuration object
 */
export const initializePWABranding = async (tenant) => {
  if (!tenant) return;

  await updatePWAManifest(tenant);
  await updatePWAIcons(tenant);
  updateThemeColor(tenant);
  updatePageTitle(tenant);

  console.log('[PWA] Branding initialized for tenant:', tenant.id || tenant.name);
};
